import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Link, useHistory } from "react-router-dom";
import { TbPlus } from "react-icons/tb";
import { PageHeaderWithBack } from "../../../../Components/Molecules";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useToast } from "../../../../Hooks/toast";
import { fetchBaasCardAccounts } from "../../../../Services/baasCardAccountsApi";
import { formatBaasConsoleConflictMessage } from "../../../../Services/baasConsoleClient";
import { BaasCardsListSkeleton } from "../components/BaasSkeleton";
import {
  BAAS_CARTOES_ACCOUNTS_CREATE_PATH,
  BAAS_CARTOES_PATH,
  buildBaasCardAccountDetailPath,
} from "../constants";
import {
  EMPTY_BAAS_CARD_ACCOUNTS_FILTERS,
  filterBaasCardAccounts,
  hasActiveBaasCardAccountsFilters,
} from "./baasCardAccountsFilters";
import { BaasCardAccountCard } from "./components/BaasCardAccountCard";
import { BaasCardAccountsFilterBar } from "./components/BaasCardAccountsFilterBar";
import { BaasCardAccountsKpiCards } from "./components/BaasCardAccountsKpiCards";

export const BaasCardAccountsPage = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { selectedInstance } = useBaasInstance();
  const isLoading = useBaasPageLoading(selectedInstance?.id);
  const { can } = useBaasCapabilities();

  const [accounts, setAccounts] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [filters, setFilters] = useState(EMPTY_BAAS_CARD_ACCOUNTS_FILTERS);

  const canCreate = can("issuer.programs.create");
  const filtersActive = hasActiveBaasCardAccountsFilters(filters);

  const filteredAccounts = useMemo(
    () => filterBaasCardAccounts(accounts, filters),
    [accounts, filters],
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!selectedInstance) {
        setAccounts([]);
        return;
      }

      setDataLoading(true);
      try {
        const next = await fetchBaasCardAccounts(selectedInstance);
        if (!cancelled) setAccounts(next);
      } catch (error) {
        if (!cancelled) {
          setAccounts([]);
          addToast({
            type: "error",
            title: formatBaasConsoleConflictMessage(
              error,
              intl.get("baas.cartoes.accounts.empty"),
            ),
          });
        }
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [addToast, selectedInstance]);

  if (isLoading || dataLoading) {
    return <BaasCardsListSkeleton count={3} withKpis withFilters />;
  }

  return (
    <div className="baas-processing min-w-0 space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <PageHeaderWithBack
            backTo={BAAS_CARTOES_PATH}
            title={intl.get("baas.cartoes.accounts.title")}
            subTitle={intl.get("baas.cartoes.accounts.subtitle")}
            className="mb-0"
          />
        </div>
        {canCreate ? (
          <Link to={BAAS_CARTOES_ACCOUNTS_CREATE_PATH} className="btn-primary shrink-0">
            <TbPlus className="h-4 w-4" />
            {intl.get("baas.cartoes.accounts.create.button")}
          </Link>
        ) : null}
      </div>

      <BaasCardAccountsKpiCards accounts={accounts} />

      <BaasCardAccountsFilterBar
        filters={filters}
        onFiltersChange={setFilters}
        filteredItems={filteredAccounts}
        accounts={accounts}
      />

      <section className="min-w-0 space-y-3">
        <p className="dashboard-section-label">
          {filteredAccounts.length === 0
            ? intl.get("baas.cartoes.accounts.listSectionEmpty")
            : intl.get("baas.cartoes.accounts.listSection", {
                count: filteredAccounts.length,
              })}
        </p>
        {filteredAccounts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-12 text-center dark:border-brand-dark-border dark:bg-brand-dark-elevated/30">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
              {filtersActive
                ? intl.get("baas.cartoes.accounts.emptyFiltered")
                : intl.get("baas.cartoes.accounts.empty")}
            </p>
            {filtersActive ? (
              <button
                type="button"
                className="btn-outline mt-4 inline-flex"
                onClick={() => setFilters(EMPTY_BAAS_CARD_ACCOUNTS_FILTERS)}
              >
                {intl.get("common.clearFilters")}
              </button>
            ) : canCreate ? (
              <Link
                to={BAAS_CARTOES_ACCOUNTS_CREATE_PATH}
                className="btn-primary mt-4 inline-flex"
              >
                <TbPlus className="h-4 w-4" />
                {intl.get("baas.cartoes.accounts.create.button")}
              </Link>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {filteredAccounts.map((account) => (
              <BaasCardAccountCard
                key={account.id}
                account={account}
                onSelect={(selected) =>
                  history.push(buildBaasCardAccountDetailPath(selected.id))
                }
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
